"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./Toast.module.css";

/**
 * Minimal portal toast. Controlled by the parent: render with a `message` to
 * show it; `onClose` fires after `duration` ms or on dismiss. Give it a
 * changing React `key` (e.g. an incrementing id) to re-trigger for repeats.
 */
export default function Toast({ message, type = "success", onClose, duration = 3500 }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => onClose?.(), duration);
    return () => clearTimeout(t);
  }, [message, duration, onClose]);

  if (!message || typeof document === "undefined") return null;

  return createPortal(
    <div className={styles.wrap} role="status" aria-live="polite">
      <div className={`${styles.toast} ${type === "error" ? styles.error : styles.success}`}>
        <span className={styles.icon} aria-hidden="true">
          {type === "error" ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="9" strokeWidth="2" />
              <path d="M12 8v5M12 16h.01" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="9" strokeWidth="2" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className={styles.message}>{message}</span>
        <button type="button" className={styles.close} onClick={() => onClose?.()} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}
